import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget. Renders only if `siteKey` provided.
 * Calls onToken(token) on success.
 */
export default function TurnstileWidget({ siteKey, onToken, theme = "dark" }) {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    if (window.turnstile) { setLoaded(true); return; }
    const existing = document.getElementById("cf-turnstile-script");
    if (existing) {
      existing.addEventListener("load", () => setLoaded(true));
      return;
    }
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => setLoaded(true);
    document.body.appendChild(s);
  }, [siteKey]);

  useEffect(() => {
    if (!loaded || !siteKey || !ref.current || !window.turnstile) return;
    if (widgetIdRef.current) {
      try { window.turnstile.remove(widgetIdRef.current); } catch {}
    }
    widgetIdRef.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      theme,
      callback: (token) => onToken && onToken(token),
      "error-callback": () => onToken && onToken(""),
      "expired-callback": () => onToken && onToken(""),
    });
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, siteKey, theme]);

  if (!siteKey) return null;
  return <div data-testid="turnstile-widget" ref={ref} className="my-3" />;
}
